import { EnabledDisabled } from '@reliance/types/enums/EnabledDisabled';
import { Cdr } from '@reliance/types/models/Cdr';
import { Category, Item, Order } from '@reliance/types/models/Commissary';
import { Configuration } from '@reliance/types/models/Configuration';
import { FacilityUser } from '@reliance/types/models/FacilityUser';
import { Inmate } from '@reliance/types/models/Inmate';
import { InmateContactResult } from '@reliance/types/models/InmateContact';
import { InmateDevice } from '@reliance/types/models/InmateDevice';
import { MobileDevice } from '@reliance/types/models/MobileDevice';
import { Group } from '@reliance/types/models/MobileDevice/Group';
import { ConnectedDisconnected } from './enums/ConnectedDisconnected';
import {
  Acknowledgement,
  AcknowledgementHistory
} from './models/acknowledgement';
import { IAuthToken } from './models/auth';
import { Block } from './models/block';
import { Booth } from './models/booth';
import { IFacility } from './models/facility';
import { IHousingUnit } from './models/housing_unit';
import {
  IInmateActivity,
  IInmateActivitySummary
} from './models/inmate_activity';
import { IInmateMail } from './models/inmate_mail';
import { IInmateRequest } from './models/inmate_request';
import { Location } from './models/location';
import { PhoneCard } from './models/phone_card';
import { RecordingBatch } from './models/recording_batch';
import { IStation } from './models/station';
import { ITextMessage } from './models/text_message';
import {
  AudioVisitation,
  IVisitationPending
} from './models/visitation';
import { IVoicemail } from './models/voicemail';
import ApiService, {
  IApiSuccessResponse,
  IPaginatedResponse
} from './services/api_service';
import config from './services/config';
import connectServerService from './services/connect_server_service';

const siteApi = new ApiService(config.api.url);
const centralApi = new ApiService(
  config.centralApi?.url || 'https://api.reliancetelephone.com'
);
let accessToken = null;

const resources = {
  setToken: (token) => {
    // This method will be called from the auth reducer when either site or central API refresh the token, so the other instance will get it as well
    accessToken = token;
    siteApi.setToken(accessToken);
    centralApi.setToken(accessToken);

    // Connect to Connect Server if configured
    if (token && config.connectServer && config.connectServer.url) {
      connectServerService.open();
    }
  },
  getToken: async (): Promise<IAuthToken> => {
    if (siteApi.isTokenExpired()) {
      return siteApi.refreshToken();
    }
    return Promise.resolve(accessToken);
  },
  getCentralGetResource<FilterType, ResponseType>(url: string) {
    return (query: Partial<FilterType>): Promise<ResponseType> => {
      return centralApi.get({ url, query });
    };
  },
  getSiteGetResource<FilterType, ResponseType>(url: string) {
    return (query: Partial<FilterType>): Promise<ResponseType> => {
      return siteApi.get({ url, query });
    };
  },

  auth: {
    postToken: (username: string, password: string) => {
      return siteApi.post({
        url: '/auth/token',
        data: {
          username,
          password,
          grant_type: 'password',
          client_id: 'relcore-site'
        }
      });
    },
    password: {
      put: (currentPassword: string, newPassword: string) => {
        return siteApi.put({
          url: '/auth/password',
          data: {
            currentPassword,
            newPassword
          }
        });
      }
    }
  },
  inmates: {
    index: (query = ({} = null)): Promise<IPaginatedResponse<Inmate>> => {
      return siteApi.get<IPaginatedResponse<Inmate>>({
        url: '/inmate?preferCache=false',
        query: query
      });
    },
    get: function (id, query?): Promise<Inmate> {
      return siteApi.get({ url: `/inmate/${id}`, query: query });
    },
    post: function (data) {
      return siteApi.post({ url: '/inmate', data: data });
    },
    put: function (id, changes) {
      return siteApi.put({ url: `/inmate/${id}`, data: changes });
    },
    delete: function (id, amount, note?) {
      return siteApi.delete({
        url: `/inmate/${id}`,
        data: { amount: amount, note: note }
      });
    },
    photoUrl: function (id) {
      return (
        config.api.url +
        '/inmate/photo/' +
        id +
        '?token=' +
        accessToken.accessToken +
        '&r=' +
        new Date()
      );
    },
    pin: function () {
      return siteApi.get({ url: '/inmate/pin' });
    }
  },
  housingUnit: {
    index: (query = ({} = null)): Promise<IPaginatedResponse<IHousingUnit>> => {
      return siteApi.get({ url: '/housing-unit', query: query });
    }
  },
  outsideParty: {
    photoUrl: function (id) {
      return (
        config.api.url +
        '/visitor/photo/' +
        id +
        '?token=' +
        accessToken.accessToken +
        '&r=' +
        new Date()
      );
    }
  },
  itds: {
    post: function (data): Promise<IApiSuccessResponse<any>> {
      return siteApi.post({ url: '/itds', data: data });
    },
    delete: function (account, data): Promise<IApiSuccessResponse<any>> {
      return siteApi.delete({ url: '/itds/' + account, data: data });
    }
  },
  inmateDevice: {
    getByInmate: function (inmateId) {
      return siteApi.get({ url: `/inmate-device?inmateId=${inmateId}` });
    },
    post: function (data) {
      return siteApi.post<InmateDevice>({ url: '/inmate-device', data: data });
    },
    delete: function (account) {
      return siteApi.delete({ url: `/inmate-device/${account}` });
    }
  },
  inmateRequest: {
    get: (id): Promise<IInmateRequest> => {
      if (id == null) {
        return Promise.resolve(null);
      }
      return centralApi.get<IInmateRequest>({ url: `/inmate-request/${id}` });
    },
    put: (
      inmateRequestId: number,
      data
    ): Promise<IApiSuccessResponse<void>> => {
      return centralApi.put({
        url: `/inmate-request/${inmateRequestId}`,
        data
      });
    },
    post: (data: FormData): Promise<IApiSuccessResponse<IInmateRequest>> => {
      return centralApi.post({
        url: '/inmate-request',
        data,
        contentType: false,
        processData: false
      });
    },
    postMultiple: (
      data: FormData
    ): Promise<IApiSuccessResponse<IInmateRequest>> => {
      return centralApi.post({
        url: '/inmate-request/multiple',
        data,
        contentType: false,
        processData: false
      });
    },
    postResponse: (
      inmateRequestId: number,
      data: FormData
    ): Promise<IApiSuccessResponse<IInmateMail>> => {
      return centralApi.post({
        url: `/inmate-request/${inmateRequestId}/response`,
        data,
        contentType: false,
        processData: false
      });
    },
    deleteAttachment: (mailId: number): Promise<IApiSuccessResponse<any>> => {
      return centralApi.delete({
        url: `/inmate-request/${mailId}/asset`
      });
    },
    pdfUrl: function (ids: string) {
      return centralApi.buildUrl('/inmate-request.pdf', {
        ids,
        includeEmails: true,
        token: accessToken.accessToken
      });
    }
  },
  visitation: {
    recordingPreviewUrl: function (id) {
      return (
        config.api.url +
        `/visitation/recording/${id}/preview?token=${accessToken.accessToken}`
      );
    },
    index: (query): Promise<IVisitationPending[]> => {
      return siteApi.get({ url: '/visitation', query: query });
    },
    post: function (data) {
      return siteApi.post({ url: '/visitation', data: data });
    },
    search: (query?): Promise<IPaginatedResponse<Cdr>> => {
      return siteApi.get({ url: '/visitation/search', query: query });
    },
    delete: function (id, note) {
      connectServerService.send({
        route: 'web/visitation/terminate',
        payload: {
          visitationId: id,
          cause: 'OfficerTerminated'
        }
      });
      return siteApi.delete({
        url: '/visitation/' + id,
        query: { note: note }
      });
    },
    checkInVisitor: function (id) {
      return siteApi.put<IVisitationPending>({
        url: `/visitation/check-in-visitor/${id}`
      });
    },
    pdfUrl: (query = {}): string => {
      return siteApi.buildUrl('/visitation/search.pdf', {
        ...query,
        token: accessToken.accessToken
      });
    }
  },
  station: {
    index: (query?): Promise<IStation[]> => {
      return siteApi
        .get({ url: '/station', query: query })
        .then((stations: IStation[]) => {
          stations = stations.map((s) => {
            s.stationStatus = {
              status: s.stationStatus.status ?? EnabledDisabled.ENABLED,
              freeswitch: s.stationStatus.freeswitch ?? ConnectedDisconnected.DISCONNECTED,
              connectServer: s.stationStatus.connectServer ?? ConnectedDisconnected.DISCONNECTED,
              inCall: s.stationStatus.inCall ?? false,
              inUse: s.stationStatus.inUse ?? false
            };
            return s;
          });
          return stations;
        });
    },
    putStatus: function (id, newStatus) {
      return siteApi.put({ url: `/station/status/${id}`, data: { newStatus } });
    },
    putMultipleStatus: function (ids, newStatus) {
      return siteApi.put({
        url: `/station/multiple/status`,
        data: { ids: ids.join(','), newStatus }
      });
    }
  },
  visitationPending: {
    index: function (query?): Promise<IPaginatedResponse<IVisitationPending>> {
      return siteApi.get({ url: '/visitation-pending', query: query });
    },
    put: function (id, note, timeLimit) {
      return siteApi.put({
        url: '/visitation-pending',
        data: { id: id, note: note, timeLimit: timeLimit }
      });
    },
    approve: function (id, note, timeLimit) {
      return siteApi.put({
        url: '/visitation-pending/approve',
        data: { id: id, note: note, timeLimit: timeLimit }
      });
    },
    reject: function (id, note) {
      return siteApi.put({
        url: '/visitation-pending/reject',
        data: { id: id, note: note }
      });
    }
  },
  mobileDevice: {
    index: function (orderBy) {
      return siteApi.get<IPaginatedResponse<MobileDevice>>({
        url: `/mobile-device?includeInmates=true&orderBy=${orderBy}&applications=Connect - Messaging&perPage=1000`
      });
    },
    getAvailable: function () {
      return siteApi.get<IPaginatedResponse<MobileDevice>>({
        url: '/mobile-device?excludeInmateAssigned=true&orderBy=name&applications=Connect - Messaging&perPage=1000'
      });
    },
    resetAppLock: function (ids) {
      return siteApi.put({
        url: `/mobile-device/${ids.join(',')}/reset-app-lock`
      });
    },
    put: function (ids, data) {
      return siteApi.put({ url: `/mobile-device/${ids.join(',')}`, data });
    },
    group: {
      index: (query = {}): Promise<IPaginatedResponse<Group>> => {
        return siteApi.get<IPaginatedResponse<Group>>({
          url: '/mobile-device/group',
          query: query
        });
      }
    }
  },
  facility: {
    putTextingLocalStatus: (status) => {
      return siteApi.put({
        url: '/facility',
        data: { textingLocalStatus: status }
      });
    },
    putCommunicationsEnabled: (enabled: boolean) => {
      return siteApi.put({
        url: '/facility',
        data: { communicationsEnabled: enabled }
      });
    },
    putAudioCallEnabled: (enabled: boolean) => {
      return siteApi.put({
        url: '/facility',
        data: { audioCallingEnabled: enabled }
      });
    },
    putVideoCallEnabled: (enabled: boolean) => {
      return siteApi.put({
        url: '/facility',
        data: { videoCallingEnabled: enabled }
      });
    }
  },
  recordingBatch: {
    index: (query = {}): Promise<IPaginatedResponse<RecordingBatch>> => {
      return siteApi.get({ url: '/recording-batch', query: query });
    },
    get: function (id) {
      return siteApi.get({ url: `/recording-batch/${id}` });
    },
    getDownloadUrl: function (id) {
      return (
        config.api.url +
        `/recording-batch/${id}/download?token=${accessToken.accessToken}`
      );
    }
  },
  audioVisitation: {
    index: (query?): Promise<IPaginatedResponse<AudioVisitation>> => {
      return siteApi.get({ url: '/audio-visitation', query: query });
    },
    get: function (id) {
      return siteApi.get({ url: `/audio-visitation/${id}` });
    },
    booth: {
      index: function (query): Promise<Booth[]> {
        return siteApi.get({ url: '/audio-visitation/booth', query: query });
      }
    }
  },
  textMessage: {
    index: (query = {}): Promise<IPaginatedResponse<ITextMessage>> => {
      return siteApi.get({ url: '/text-message', query: query });
    },
    excelIndexUrl: function (query) {
      return siteApi.buildUrl('/text-message.xls', {
        ...query,
        token: accessToken.accessToken
      });
    },
    pdfUrl: (query = {}): string => {
      return siteApi.buildUrl('/text-message.pdf', {
        ...query,
        token: accessToken.accessToken
      });
    },
    asset: {
      delete: (id, data): Promise<IApiSuccessResponse<{ id: number, message: string }>> => {
        return centralApi.delete({ url: `/text-message/${id}/asset`, query: data });
      }
    }
  },
  phoneCard: {
    get: (id: string): Promise<PhoneCard> => {
      return siteApi.get({ url: `/phone-card/${id}` });
    }
  },
  location: {
    getAniDetails: (ani): Promise<Location> => {
      return siteApi.get({ url: `/location/ani-details/${ani}` });
    }
  },
  privateNumber: {
    post: (data): Promise<IApiSuccessResponse<void>> => {
      return centralApi.post({ url: '/private-number', data: data });
    },
    delete: (id, data): Promise<IApiSuccessResponse<void>> => {
      return centralApi.delete({ url: `/private-number/${id}`, query: data });
    }
  },
  cdr: {
    get: function (id) {
      return siteApi.get({ url: `/cdr/${id}` });
    },
    index: (query = {}): Promise<IPaginatedResponse<Cdr>> => {
      return siteApi.get({ url: '/cdr', query: query });
    },
    recordingUrl: function (recordingUrl) {
      if (recordingUrl == null) {
        return null;
      }

      if (recordingUrl.indexOf('http') == -1) {
        recordingUrl = config.api.url + recordingUrl;
      }

      var separator = recordingUrl.indexOf('?') > -1 ? '&' : '?';
      return recordingUrl + separator + 'token=' + accessToken.accessToken;
    },
    excelIndexUrl: function (query) {
      return siteApi.buildUrl('/cdr.xls', {
        ...query,
        token: accessToken.accessToken
      });
    },
    pdfUrl: (query = {}): string => {
      return siteApi.buildUrl('/cdr.pdf', {
        ...query,
        token: accessToken.accessToken
      });
    }
  },
  voicemail: {
    get: function (id) {
      return siteApi.get({ url: `/voicemail/${id}` });
    },
    index: (query = {}): Promise<IPaginatedResponse<IVoicemail>> => {
      return siteApi.get<IPaginatedResponse<IVoicemail>>({
        url: '/voicemail',
        query: query
      });
    },
    pdfUrl: (query = {}): string => {
      return siteApi.buildUrl('/voicemail.pdf', {
        ...query,
        token: accessToken.accessToken
      });
    }
  },
  block: {
    index: (query = {}): Promise<IPaginatedResponse<Block>> => {
      return siteApi
        .get<IPaginatedResponse<Block>>({ url: '/block', query: query })
        .then((res) => {
          let normalizedData = res;
          // Support v1 formatted responses ("blocks" instead of "data")
          if (res.data === undefined) {
            normalizedData = res;
            normalizedData.data = (res as any).blocks;
          }
          return normalizedData;
        });
    },
    post: (data): Promise<IApiSuccessResponse<void>> => {
      return siteApi.post({ url: '/block', data: data });
    },
    delete: (ani, query): Promise<IApiSuccessResponse<void>> => {
      return siteApi.delete({ url: `/block/${ani}`, query });
    }
  },
  inmateAccount: {
    activity: {
      index: (query): Promise<IPaginatedResponse<IInmateActivity>> => {
        return centralApi.get({
          url: '/inmate-account/activity',
          query: query
        });
      },
      post: function (data) {
        return siteApi.post({ url: '/inmate-account/activity', data: data });
      }
    },
    summary: (
      query = {}
    ): Promise<IPaginatedResponse<IInmateActivitySummary>> => {
      return siteApi.get({ url: '/inmate-account/summary', query: query });
    }
  },
  inmateNotification: {
    post: function (data) {
      return centralApi.post({ url: '/inmate-notification', data: data });
    },
    delete: function (id) {
      return centralApi.delete({ url: `/inmate-notification/${id}` });
    }
  },
  inmateContact: {
    index: function (query) {
      return siteApi.get<IPaginatedResponse<InmateContactResult>>({
        url: '/inmate-contact',
        query: query
      });
    },
    post: (data) => {
      return centralApi.post({ url: '/inmate-contact', data });
    },
    put: (id, data) => {
      return centralApi.put({ url: `/inmate-contact/by-id/${id}`, data });
    }
  },
  configuration: {
    get: function (key?): Promise<Configuration> {
      return siteApi.get({ url: '/configuration', query: { key } });
    }
  },
  facilityUser: {
    index: (
      query = ({} = null)
    ): Promise<IPaginatedResponse<FacilityUser>> => {
      return siteApi.get<IPaginatedResponse<FacilityUser>>({
        url: '/facility-user',
        query: query
      });
    },
    post: function (data = null) {
      return siteApi.post({ url: '/facility-user', data: data });
    },
    put: (id, data = null) => {
      return siteApi.put({ url: `/facility-user/${id}`, data: data });
    }
  },
  commissary: {
    category: {
      index: (
        query = ({} = null)
      ): Promise<IPaginatedResponse<Category>> => {
        return centralApi.get<IPaginatedResponse<Category>>({
          url: '/commissary/category',
          query: query
        });
      },
      post: (data = null) => {
        return centralApi.post({
          url: '/commissary/category',
          data,
          contentType: false,
          processData: false
        });
      },
      put: async (id, data: FormData = null) => {
        // If a photo was supplied, we have to send that using a separate POST request
        if (data.get('categoryPhoto[0]')) {
          await centralApi.post({
            url: `/commissary/category/${id}/photo`,
            data: data,
            contentType: false,
            processData: false
          });
        }
        return centralApi.put({
          url: `/commissary/category/${id}`,
          data: { name: data.get('name') }
        });
      }
    },
    item: {
      index: (
        query = ({} = null)
      ): Promise<IPaginatedResponse<Item>> => {
        return centralApi.get<IPaginatedResponse<Item>>({
          url: '/commissary/item',
          query: query
        });
      },
      post: (data = null) => {
        return centralApi.post({
          url: '/commissary/item',
          data,
          contentType: false,
          processData: false
        });
      },
      put: async (id, data: FormData = null) => {
        // If a photo was supplied, we have to send that using a separate POST request
        if (data.get('itemPhoto[0]')) {
          await centralApi.post({
            url: `/commissary/item/${id}/photo`,
            data: data,
            contentType: false,
            processData: false
          });
        }
        return centralApi.put({
          url: `/commissary/item/${id}`,
          data: {
            name: data.get('name'),
            description: data.get('description'),
            price: data.get('price'),
            itemNumber: data.get('itemNumber'),
            billingCategory: data.get('billingCategory'),
            active: data.get('active')
          }
        });
      }
    },
    order: {
      get: (id: number = null): Promise<Order> => {
        if (id == null) {
          return Promise.resolve(null);
        }
        return siteApi.get<Order>({
          url: '/commissary/order/' + id
        });
      },
      index: (
        query = ({} = null)
      ): Promise<IPaginatedResponse<Order>> => {
        return siteApi.get<IPaginatedResponse<Order>>({
          url: '/commissary/order',
          query: query
        });
      },
      pdfUrl: function (ids: string) {
        return siteApi.buildUrl('/commissary/order.pdf', {
          ids,
          token: accessToken.accessToken
        });
      }
    }
  },
  acknowledgement: {
    index: (
      query = ({} = null)
    ): Promise<IPaginatedResponse<Acknowledgement>> => {
      return siteApi.get<IPaginatedResponse<Acknowledgement>>({
        url: '/acknowledgement',
        query: query
      });
    },
    history: {
      index: (
        query = ({} = null)
      ): Promise<IPaginatedResponse<AcknowledgementHistory>> => {
        return siteApi.get<IPaginatedResponse<AcknowledgementHistory>>({
          url: '/acknowledgement/history',
          query: query
        });
      }
    }
  },
  website: {
    getBaseData: (hostname: string) =>
      siteApi.get<IFacility>({ url: '/facility/by-site-url/' + hostname })
  }
};

export default resources;
